import React from "react";
import {
  Box,
  Typography,
  Paper,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { NoticeboardButton, HeaderButton } from "../shared";
import { lightTheme } from "../../styles/theme";

// Format organization ID to a readable name
// Converts "UDOBO-PRE-PRIMARY-SCHOOL-DEMO-ACCOUNT" to "Udobo Pre Primary School Demo Account"
const formatOrganizationName = (orgId?: string): string => {
  if (!orgId) return "";
  return orgId
    .toLowerCase()
    .replace(/-/g, " ") // Replace hyphens with spaces
    .split(" ")
    .filter((word) => word.length > 0) // Remove empty strings
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize first letter
    .join(" ");
};

const createWelcomeContent = (
  schoolName?: string,
  userType?: "admin" | "team",
  fullName?: string,
  orgId?: string
) => {
  let title;
  if (userType === "team" && orgId) {
    const formattedOrgName = formatOrganizationName(orgId);
    const name = fullName?.trim();
    title = name
      ? `Hi ${name},\nWelcome to ${formattedOrgName}`
      : `Welcome to ${formattedOrgName}`;
  } else if (schoolName) {
    title = `Hello ${schoolName}`;
  } else {
    title = "Welcome to Noticeboard";
  }

  return {
    title,
    description: "Manage notices, events, and communication all in one place.",
  };
};

interface QuickAction {
  label: string;
  onClick: () => void;
  variant: string;
}

interface FeaturedCarouselProps {
  schoolName?: string;
  userType?: "admin" | "team";
  fullName?: string;
  orgId?: string;
  quickActions?: QuickAction[];
}

const getQuickActionTooltip = (label: string): string => {
  switch (label) {
    case "Create Notice":
      return "Start creating and publishing a new notice";
    case "Send Notice":
      return "Send a notice in seconds";
    case "Direct Message":
    case "Direct Manage":
      return "Send a notice directly to an individual user";
    case "View Notices":
      return "Open Notices to view and manage notices";
    case "Manage Users":
    case "Users":
      return "Go to Users to invite and manage users";
    case "Manage Circles":
    case "Circles":
      return "Manage your circles and structure";
    case "Create an event":
      return "Open the event creation flow";
    case "Upload to Drive":
      return "Upload files to your drive";
    default:
      return `Open ${label}`;
  }
};

const FeaturedCarousel: React.FC<FeaturedCarouselProps> = ({
  schoolName,
  userType,
  fullName,
  orgId,
  quickActions,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const welcomeContent = createWelcomeContent(
    schoolName,
    userType,
    fullName,
    orgId
  );

  const tealGradientBackground = `linear-gradient(135deg, ${lightTheme.colors.primary} 0%, ${lightTheme.colors.primaryAltDark} 100%)`;

  // Simple mobile version
  if (isMobile) {
    return (
      <Paper
        elevation={0}
        sx={{
          background: tealGradientBackground,
          borderRadius: 2,
          p: 3,
          color: "common.white",
          textAlign: "center",
          mb: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            mb: 1,
            fontSize: "1.25rem",
            whiteSpace: "pre-line",
          }}
        >
          {welcomeContent.title}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            mb: 2,
            opacity: 0.9,
            fontSize: (theme) => theme.typography.body2.fontSize,
            lineHeight: 1.4,
          }}
        >
          {welcomeContent.description}
        </Typography>

        {quickActions && (
          <Box sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "stretch" }}>
            {quickActions.map((action) =>
              action.variant === "primary" ? (
                <HeaderButton
                  key={action.label}
                  onClick={action.onClick}
                  size="compact"
                  sx={{
                    flex: 1,
                    py: 1,
                    fontSize: (theme) => theme.typography.body2.fontSize,
                    fontWeight: 600,
                    minWidth: 0,
                  }}
                >
                  {action.label}
                </HeaderButton>
              ) : (
                <NoticeboardButton
                  key={action.label}
                  variant="secondary"
                  onClick={action.onClick}
                  sx={{
                    flex: 1,
                    py: 1,
                    fontSize: (theme) => theme.typography.body2.fontSize,
                    fontWeight: 500,
                    minWidth: 0,
                    bgcolor: "common.white",
                    color: "primary.main",
                    border: "1px solid",
                    borderColor: "primary.main",
                    "&:hover": {
                      bgcolor: lightTheme.colors.background,
                    },
                  }}
                >
                  {action.label}
                </NoticeboardButton>
              )
            )}
          </Box>
        )}
      </Paper>
    );
  }

  // Desktop version (original design)
  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        minHeight: quickActions ? 200 : 180,
        height: quickActions ? "auto" : 180,
        borderRadius: 2,
        overflow: "hidden",
        background: tealGradientBackground,
        boxShadow: `0 2px 12px ${lightTheme.colors.inputFocusShadow}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: quickActions ? 200 : 180,
          px: 4,
          py: 2.5,
          textAlign: "center",
          color: "common.white",
        }}
      >
        <Typography
          variant="h1"
          component="h2"
          sx={{
            fontWeight: 600,
            fontSize: 26,
            mb: 1.5,
            textShadow: `0 1px 3px ${lightTheme.colors.textShadowMedium}`,
            lineHeight: 1.4,
            whiteSpace: "pre-line",
          }}
        >
          {welcomeContent.title}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            fontSize: (theme) => theme.typography.body1.fontSize,
            maxWidth: 600,
            opacity: 0.95,
            textShadow: `0 1px 2px ${lightTheme.colors.textShadowMedium}`,
            mb: quickActions ? 2 : 0,
            lineHeight: 1.6,
          }}
        >
          {welcomeContent.description}
        </Typography>

        {quickActions && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 1.5,
              rowGap: 1.25,
              mt: 2,
              justifyContent: "center",
              maxWidth: "100%",
            }}
          >
            {quickActions.map((action) =>
              action.variant === "primary" ? (
                <Tooltip key={action.label} title={getQuickActionTooltip(action.label)} arrow>
                  <Box>
                    <HeaderButton
                      onClick={action.onClick}
                      size="compact"
                      sx={{
                        py: 0.75,
                        px: 2,
                        borderRadius: 2,
                        fontWeight: 600,
                      }}
                    >
                      {action.label}
                    </HeaderButton>
                  </Box>
                </Tooltip>
              ) : (
                <Tooltip key={action.label} title={getQuickActionTooltip(action.label)} arrow>
                  <Box>
                    <NoticeboardButton
                      variant="secondary"
                      onClick={action.onClick}
                      sx={{
                        fontWeight: 500,
                        py: 0.75,
                        px: 2,
                        borderRadius: 2,
                        textTransform: "none",
                        textShadow: "none",
                        fontSize: (th) => th.typography.body2.fontSize,
                        bgcolor: lightTheme.colors.headerButtonActive,
                        color: "common.white",
                        border: `1px solid ${lightTheme.colors.whiteOverlay30}`,
                        "&:hover": {
                          bgcolor: lightTheme.colors.whiteOverlay25,
                          borderColor: lightTheme.colors.whiteOverlay50,
                          transform: "translateY(-1px)",
                        },
                        "&:active": {
                          transform: "translateY(0)",
                        },
                        "&:focus-visible": {
                          outline: "2px solid white",
                          outlineOffset: "2px",
                        },
                      }}
                    >
                      {action.label}
                    </NoticeboardButton>
                  </Box>
                </Tooltip>
              )
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default FeaturedCarousel;
